import React, { useEffect, useMemo, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import { merchantApi } from '@/services/api'
import type { MerchantCategory } from '@/types'

interface BusinessOnboardingModalProps {
  isOpen: boolean
  onClose: () => void
  onComplete: () => void
}

type Step = 'intro' | 'form' | 'success'

interface BusinessApplicationForm {
  business_name: string
  tax_number?: string
  address: string
  contact_phone: string
  contact_email: string
}

const MAX_PHOTOS = 6

const isCategoryListResponse = (value: unknown): value is { results: MerchantCategory[] } => {
  if (typeof value !== 'object' || value === null) return false
  if (!('results' in value)) return false
  return Array.isArray((value as { results?: unknown }).results)
}

export const BusinessOnboardingModal: React.FC<BusinessOnboardingModalProps> = ({
  isOpen,
  onClose,
  onComplete,
}) => {
  const { t } = useTranslation()
  const [step, setStep] = useState<Step>('intro')
  const [categories, setCategories] = useState<MerchantCategory[]>([])
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set())
  const [photos, setPhotos] = useState<File[]>([])
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<BusinessApplicationForm>({
    mode: 'onBlur',
  })

  useEffect(() => {
    if (!isOpen) return
    setStep('intro')
    setSelectedCategories(new Set())
    setPhotos([])
    setPhotoPreviews([])
    reset()

    const loadCategories = async () => {
      try {
        const response = await merchantApi.getCategories()
        const payload = response.data as { data?: unknown } | unknown
        const nested = (payload as { data?: unknown })?.data ?? payload
        let results: MerchantCategory[] = []

        if (Array.isArray(nested)) {
          results = nested
        } else if (isCategoryListResponse(nested)) {
          results = nested.results
        }

        setCategories(results)
      } catch (error) {
        console.error('Failed to load categories:', error)
        toast.error(t('businessOnboarding.categoryLoadError', 'Failed to load categories'))
      }
    }

    loadCategories()
  }, [isOpen, reset, t])

  const categoryLabel = (category: MerchantCategory) => {
    const key = `businessOnboarding.categoryLabels.${category.slug}`
    const translated = t(key)
    return translated !== key ? translated : category.name
  }

  const toggleCategory = (categoryId: string) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev)
      if (next.has(categoryId)) {
        next.delete(categoryId)
      } else {
        next.add(categoryId)
      }
      return next
    })
  }

  const handlePhotoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    if (!files.length) return

    const filtered = files.filter((file) => file.type.startsWith('image/'))
    if (filtered.length !== files.length) {
      toast.error(t('businessOnboarding.photosImageOnly', 'Only image files are allowed'))
    }

    setPhotos((prev) => {
      const combined = [...prev, ...filtered].slice(0, MAX_PHOTOS)
      if (combined.length > MAX_PHOTOS) {
        toast.error(t('businessOnboarding.photosLimit', 'You can upload up to 6 photos'))
      }
      return combined
    })
    setPhotoPreviews((prev) => {
      const previews = filtered.map((file) => URL.createObjectURL(file))
      const combined = [...prev, ...previews].slice(0, MAX_PHOTOS)
      if (combined.length > MAX_PHOTOS) {
        previews.slice(MAX_PHOTOS - prev.length).forEach((url) => URL.revokeObjectURL(url))
      }
      return combined
    })
    event.target.value = ''
  }

  const removePhoto = (index: number) => {
    setPhotos((prev) => prev.filter((_, idx) => idx !== index))
    setPhotoPreviews((prev) => {
      const url = prev[index]
      if (url) {
        URL.revokeObjectURL(url)
      }
      return prev.filter((_, idx) => idx !== index)
    })
  }

  const selectedCategoryIds = useMemo(() => Array.from(selectedCategories), [selectedCategories])

  const onSubmit = async (data: BusinessApplicationForm) => {
    if (selectedCategoryIds.length === 0) {
      toast.error(t('businessOnboarding.categoryRequired', 'Select at least one category'))
      return
    }
    if (photos.length === 0) {
      toast.error(t('businessOnboarding.photosRequired', 'Please upload business photos'))
      return
    }

    setIsSubmitting(true)
    try {
      const formData = new FormData()
      formData.append('business_name', data.business_name.trim())
      formData.append('tax_number', data.tax_number?.trim() || '')
      formData.append('address', data.address.trim())
      formData.append('contact_phone', data.contact_phone.trim())
      formData.append('contact_email', data.contact_email.trim().toLowerCase())
      selectedCategoryIds.forEach((id) => formData.append('category_ids', id))
      photos.forEach((photo) => formData.append('photos', photo))

      await merchantApi.submitBusinessApplication(formData)
      toast.success(t('businessOnboarding.submitSuccess', 'Business application submitted'))
      setStep('success')
    } catch (error: any) {
      const message =
        error?.response?.data?.error ||
        t('businessOnboarding.submitError', 'Failed to submit business application')
      toast.error(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      photoPreviews.forEach((url) => URL.revokeObjectURL(url))
      onClose()
    }
  }

  const handleComplete = () => {
    onComplete()
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="business-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto"
          onClick={(event) => {
            if (event.target === event.currentTarget && !isSubmitting) {
              handleClose()
            }
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-white rounded-3xl shadow-2xl max-w-md w-full max-h-[85vh] sm:max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10 flex items-center justify-between">
              <h2 className="text-xl font-bold text-text-primary">
                {t('businessOnboarding.title', 'Merchant onboarding')}
              </h2>
              <button
                onClick={handleClose}
                className="p-2 rounded-full text-text-secondary hover:text-text-primary"
                disabled={isSubmitting}
                aria-label={t('common.close', 'Close')}
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {step === 'intro' && (
                <div className="space-y-6 text-center">
                  <div className="rounded-2xl overflow-hidden shadow-lg">
                    <img
                      src="/assets/imagecompanys.png"
                      alt={t('businessOnboarding.introImageAlt', 'Business registration')}
                      className="w-full h-auto object-cover"
                    />
                  </div>
                  <div className="space-y-3">
                    <h3 className="text-2xl font-bold text-text-primary">
                      {t('businessOnboarding.mapBenefitTitle', 'Be visible on the Djelo map')}
                    </h3>
                    <p className="text-text-secondary">
                      {t(
                        'businessOnboarding.mapBenefitDescription',
                        'Merchants can showcase their business and attract new customers directly from the Djelo map.'
                      )}
                    </p>
                    <p className="text-text-secondary">
                      {t(
                        'businessOnboarding.introMessage',
                        'If you are a business, register as such and you will appear on the Djelo merchant map.'
                      )}
                    </p>
                  </div>
                  <Button onClick={() => setStep('form')} className="w-full">
                    {t('businessOnboarding.startApplication', 'Start application')}
                  </Button>
                </div>
              )}

              {step === 'form' && (
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <Input
                    type="text"
                    placeholder={t('businessOnboarding.businessName', 'Business name')}
                    {...register('business_name', {
                      required: t('businessOnboarding.required', 'This field is required'),
                      minLength: {
                        value: 2,
                        message: t('businessOnboarding.businessNameMin', 'Business name is too short'),
                      },
                    })}
                    error={errors.business_name?.message}
                  />

                  <Input
                    type="text"
                    placeholder={`${t('businessOnboarding.taxNumber', 'Tax number')} (${t('common.optional', 'optional')})`}
                    {...register('tax_number')}
                  />

                  <div className="space-y-1">
                    <Textarea
                      placeholder={t('businessOnboarding.address', 'Business address')}
                      {...register('address', {
                        required: t('businessOnboarding.required', 'This field is required'),
                        minLength: {
                          value: 5,
                          message: t('businessOnboarding.addressMin', 'Address is too short'),
                        },
                      })}
                    />
                    {errors.address?.message && (
                      <p className="text-sm text-error">{errors.address.message}</p>
                    )}
                  </div>

                  <Input
                    type="tel"
                    placeholder={t('businessOnboarding.contactPhone', 'Contact phone')}
                    {...register('contact_phone', {
                      required: t('businessOnboarding.required', 'This field is required'),
                      minLength: {
                        value: 7,
                        message: t('businessOnboarding.phoneInvalid', 'Invalid phone number'),
                      },
                    })}
                    error={errors.contact_phone?.message}
                  />

                  <Input
                    type="email"
                    placeholder={t('businessOnboarding.contactEmail', 'Contact email')}
                    {...register('contact_email', {
                      required: t('businessOnboarding.required', 'This field is required'),
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: t('businessOnboarding.emailInvalid', 'Invalid email address'),
                      },
                    })}
                    error={errors.contact_email?.message}
                  />

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-text-primary">
                      {t('businessOnboarding.categories', 'Categories')}
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {categories.map((category) => (
                        <label
                          key={category.id}
                          className="flex items-center gap-2 border rounded-lg px-3 py-2 text-sm cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedCategories.has(category.id)}
                            onChange={() => toggleCategory(category.id)}
                            className="accent-primary"
                          />
                          <span>{categoryLabel(category)}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-text-primary">
                      {t('businessOnboarding.photos', 'Business photos')}
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handlePhotoSelect}
                      className="block w-full text-sm text-text-secondary file:mr-4 file:rounded-lg file:border-0 file:bg-primary/10 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-primary hover:file:bg-primary/20"
                    />
                    <p className="text-xs text-text-secondary">
                      {t('businessOnboarding.photosHint', 'Upload up to 6 photos')}
                    </p>
                    {photoPreviews.length > 0 && (
                      <div className="flex gap-3 overflow-x-auto pb-2">
                        {photoPreviews.map((previewUrl, index) => (
                          <div
                            key={`${previewUrl}-${index}`}
                            className="relative min-w-[120px] h-[90px] rounded-lg overflow-hidden border border-gray-200"
                          >
                            <img
                              src={previewUrl}
                              alt={t('businessOnboarding.photoPreviewAlt', 'Business photo preview')}
                              className="w-full h-full object-cover"
                            />
                            {isSubmitting && (
                              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              </div>
                            )}
                            {!isSubmitting && (
                              <button
                                type="button"
                                onClick={() => removePhoto(index)}
                                className="absolute top-1 right-1 bg-white/80 text-gray-700 rounded-full px-2 text-xs"
                              >
                                {t('common.delete', 'Delete')}
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setStep('intro')}
                      className="flex-1"
                      disabled={isSubmitting}
                    >
                      {t('common.back', 'Back')}
                    </Button>
                    <Button type="submit" className="flex-1" loading={isSubmitting}>
                      {isSubmitting
                        ? t('businessOnboarding.submitting', 'Submitting...')
                        : t('businessOnboarding.submit', 'Submit')}
                    </Button>
                  </div>
                </form>
              )}

              {step === 'success' && (
                <div className="space-y-4 text-center">
                  <h3 className="text-2xl font-bold text-text-primary">
                    {t('businessOnboarding.successTitle', 'Application received')}
                  </h3>
                  <p className="text-text-secondary">
                    {t(
                      'businessOnboarding.successMessage',
                      'Your business application was submitted. Please wait for approval of your business account.'
                    )}
                  </p>
                  <Button onClick={handleComplete} className="w-full">
                    {t('common.finish', 'Finish')}
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
